import {closeCurrentDialog, openDeleteDialog, openDialog} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {forwardRef, useImperativeHandle, useState} from 'react';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  PatchReceiveNoteBulkEditRequest,
  useBulkDeleteReceiveNoteMutation,
  usePatchReceiveNoteBulkEditMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  handleApiError,
  logError,
  refreshDmsDatagrid,
} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import {ExternalBulkActionsBar, type ActionCallback, type GridDataChange} from 'features/eag-grid';

import {RECEIVE_NOTE_PDF_COLUMNS} from '../../../constants/RECEIVE_NOTE_PDF_COLUMNS';
import {useReceiveNotePdfExport} from '../../../hooks/useReceiveNotePdfExport';
import {ReceiveNoteListBulkEdit} from './ReceiveNoteListBulkEdit';

export type ReceiveNoteListHandle = {
  refreshDataGrid: () => void;
};

export const ReceiveNoteList = forwardRef<ReceiveNoteListHandle>((_, ref) => {
  const navigate = useNavigate();
  useImperativeHandle(
    ref,
    () => ({
      refreshDataGrid: () => refreshDmsDatagrid('receive-note'),
    }),
    []
  );

  const [gridData, setGridData] = useState<GridDataChange | null>(null);
  const onPdfExportCallback = useReceiveNotePdfExport();

  const [bulkDeleteReceiveNote] = useBulkDeleteReceiveNoteMutation();
  const [bulkEditReceiveNote] = usePatchReceiveNoteBulkEditMutation();

  const handleBulkEditReceiveNote = async (requestBody: PatchReceiveNoteBulkEditRequest) => {
    await bulkEditReceiveNote(requestBody)
      .unwrap()
      .then(closeCurrentDialog)
      .then(() => refreshDmsDatagrid('receive-note'))
      .catch(handleApiError);
  };

  const actionCallback: ActionCallback = ({actionKey, rowId, sourceSearchParams, refreshData}) => {
    const isBulk = isArray(rowId);
    match({actionKey, isBulk})
      .with(
        {actionKey: 'redirectDetail', isBulk: false},
        {actionKey: 'edit', isBulk: false},
        () => {
          const id = isArray(rowId) ? head(rowId) : rowId;

          if (isNil(id)) {
            return logError('Not able to redirect, receiveNoteId is not defined');
          }

          navigate(
            composePath(warehouseRoutes.receiveNoteDetailOverview, {
              queryParams: sourceSearchParams,
              params: {id},
              isSearchParamsPreserved: false,
            })
          );
        }
      )
      .with({actionKey: 'edit', isBulk: true}, () => {
        openDialog(
          <ReceiveNoteListBulkEdit
            rowId={rowId}
            onSubmit={handleBulkEditReceiveNote}
            data-testid={testIds.warehouse.receiveNoteList('dialogs.bulkEdit')}
          />,
          {
            size: 'small',
            title: i18n.t('entity.warehouse.labels.bulkEdit'),
          }
        );
      })
      .with({actionKey: 'delete'}, () => {
        const ids = isArray(rowId) ? rowId : [rowId];

        openDeleteDialog({
          id: 'deleteReceiveNoteDialog',
          text: i18n.t('entity.warehouse.labels.deleteSelectedItemsPrompt'),
          onConfirm: async () =>
            await bulkDeleteReceiveNote({receiveNoteId: ids})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
          'data-testid': testIds.warehouse.receiveNoteList('deleteReceiveNoteDialog'),
        });
      })
      .with({actionKey: 'corrective'}, () => {
        const id = isArray(rowId) ? head(rowId) : rowId;

        navigate(
          composePath(warehouseRoutes.receiveNoteCorrectionCreate, {
            params: {receiveNoteCreateId: id},
          })
        );
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <VStack height="100%">
      <Show whenFeatureEnabled={featureFlags.EAG_GRID_EXPERIMENTAL_EXTERNAL_BULK_ACTIONS}>
        <HStack justify="flex-end">
          <ExternalBulkActionsBar
            gridCode="receive-note"
            data={gridData}
            data-testid={testIds.warehouse.receiveNoteList('receiveNoteBulkActions')}
          />
        </HStack>
      </Show>
      <Box flex={1}>
        <DmsDataGrid
          gridCode="receive-note"
          actionCallback={actionCallback}
          onGridDataChange={setGridData}
          pdfColumns={RECEIVE_NOTE_PDF_COLUMNS}
          onPdfExportCallback={onPdfExportCallback}
          data-testid={testIds.warehouse.receiveNoteList('receiveNotePendingList')}
        />
      </Box>
    </VStack>
  );
});
